import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import QuizIcon from '@mui/icons-material/Quiz';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SubscriptionList from '../../components/SubscriptionList';
import api from '../../api';
import { toast } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';

const ParentBilling = () => {
  const { parentId, billingType: billingTypeParam } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [mockPurchases, setMockPurchases] = useState([]);
  const [parentName, setParentName] = useState('');

  const billingType = billingTypeParam === 'mock' ? 'mock' : 'course';
  const isCoursePage = billingType === 'course';
  const pageTitle = isCoursePage ? 'Parent Course Purchases' : 'Parent Mock Exam Purchases';

  useEffect(() => {
    if (!parentId) return;
    fetchParentDetails();
    if (isCoursePage) {
      fetchCourseSubscriptions();
      return;
    }
    fetchMockPurchases();
  }, [parentId, billingType]);

  const fetchParentDetails = async () => {
    try {
      const response = await api.get(`admin/parent/${parentId}`);
      if (response.data?.success) {
        const parent = response.data?.data || {};
        const full = `${parent.first_name || ''} ${parent.last_name || ''}`.trim();
        setParentName(full || parent.full_name || '');
      } else {
        setParentName('');
      }
    } catch (error) {
      console.error('Error fetching parent details:', error);
      setParentName('');
    }
  };

  const fetchCourseSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await api.get(`admin/parent/${parentId}/subscriptions`);
      if (response.data?.success) {
        setSubscriptions(response.data.data || []);
      } else {
        setSubscriptions([]);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast.error('Failed to load course subscriptions');
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMockPurchases = async () => {
    setLoading(true);
    try {
      const response = await api.get(`admin/parent/${parentId}/mock-exam-purchases`);
      if (response.data?.success) {
        setMockPurchases(response.data.data || []);
      } else {
        setMockPurchases([]);
      }
    } catch (error) {
      console.error('Error fetching mock exam purchases:', error);
      toast.error('Failed to load mock exam purchases');
      setMockPurchases([]);
    } finally {
      setLoading(false);
    }
  };

  const mockRows = useMemo(() => (Array.isArray(mockPurchases) ? mockPurchases : []), [mockPurchases]);

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/admin/billing-transactions')}
        sx={{
          mb: 3,
          color: '#5a6c7d',
          fontWeight: 600,
          '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
        }}
      >
        Back to Billing & Transactions
      </Button>

      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.5,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 2,
            px: 2.5,
            py: 1,
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.25)',
          }}
        >
          {isCoursePage ? <SchoolIcon sx={{ color: 'white' }} /> : <QuizIcon sx={{ color: 'white' }} />}
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
            {pageTitle}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 0.5 }}>
          Parent: <strong>{parentName || `ID ${parentId}`}</strong>
        </Typography>
      </Box>

      <Card
        sx={{
          borderRadius: 4,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(20px)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 3.5, bgcolor: '#f8fafc', minHeight: 480 }}>
          {isCoursePage ? (
            <SubscriptionList
              subscriptions={subscriptions}
              loading={loading}
              showPayColumn={false}
              billingParentId={parentId != null ? Number(parentId) : null}
            />
          ) : loading ? (
            <Typography color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 8 }}>
              Loading mock exam purchases...
            </Typography>
          ) : mockRows.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No mock exam purchases found
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Mock Exam</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>School</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Grade / year</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Subject(s)</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Payment</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Purchased At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockRows.map((row) => (
                    <TableRow key={row.purchase_id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.exam_name || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.duration_minutes || 0} min | {row.total_marks || 0} marks
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 160 }}>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {row.school_name || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.grade_name || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {row.subject_names || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.student_name || 'Not Assigned'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.student_email || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {Number(row.price || 0).toFixed(2)} {row.currency || 'EUR'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.payment_status || 'PENDING'}
                          color={(row.payment_status || '').toUpperCase() === 'PAID' ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{row.purchased_at || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default ParentBilling;

